"use client";

import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction } from "next-safe-action/hooks";
import { motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";

import {
  ContactFormData,
  ContactFormResponse,
} from "@/types/communication/contact";
import { contactFormSchema } from "@/lib/validations/communication/contact";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AnimatedCheckmark } from "@/components/ui/animated-checkmark";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { X, Paperclip, File } from "lucide-react";
import Image from "next/image";
import { AppFile } from "@/types";
import { z } from "zod";
import { PhoneInput } from "@/components/ui/phone-input";

interface ContactFormProps {
  userInfo?: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company?: string;
  };
  submitContactFormAction: (
    data: ContactFormData
  ) => Promise<{ data?: ContactFormResponse }>;
}

export default function ContactForm({
  userInfo,
  submitContactFormAction,
}: ContactFormProps) {
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);
  const [prefilled, setPrefilled] = useState<Record<string, boolean>>({});
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize form with user data if available
  const defaultName = userInfo
    ? `${userInfo.firstName} ${userInfo.lastName}`.trim()
    : "";

  // Use the zod schema type for the form
  const form = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: defaultName,
      email: userInfo?.email || "",
      message: "",
      phone: userInfo?.phone || "",
      company: userInfo?.company || "",
      attachment: undefined,
    },
  });

  // Determine which fields were prefilled
  useEffect(() => {
    setPrefilled({
      name: !!defaultName,
      email: !!userInfo?.email,
      phone: !!userInfo?.phone,
      company: !!userInfo?.company,
    });
  }, [defaultName, userInfo]);

  // Use the safe action hook
  const { execute, status } = useAction(submitContactFormAction, {
    onSuccess: (data) => {
      const result = data.data as ContactFormResponse;
      if (result?.success) {
        toast({
          title: "Message Sent",
          description:
            "Thank you for your message. We'll get back to you soon.",
          variant: "success",
        });
        form.reset({
          name: defaultName,
          email: userInfo?.email || "",
          message: "",
          phone: userInfo?.phone || "",
          company: userInfo?.company || "",
          attachment: undefined,
        });
        setIsSuccess(true);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            result?.error || "Failed to send message. Please try again.",
        });
      }
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send message. Please try again.",
      });
      console.error("Failed to send contact form:", error);
    },
  });

  const onSubmit = (values: z.infer<typeof contactFormSchema>) => {
    execute(values);
  };

  const handleNewMessage = () => {
    setIsSuccess(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);

    if (!file) return;

    // Check file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setFileError("File size exceeds 5MB limit");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Check file type
    const acceptedImageTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/jpg",
    ];
    const isPDF = file.type === "application/pdf";
    const isAcceptedImage = acceptedImageTypes.includes(file.type);

    if (!isPDF && !isAcceptedImage) {
      setFileError("Only PDFs and images (JPG, PNG, GIF) are allowed");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Convert file to base64 data URL instead of blob URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;

      // Create an AppFile object
      const appFile: AppFile = {
        id: uuidv4(),
        name: file.name.split(".")[0],
        type: file.name.split(".").pop() || "",
        url: dataUrl,
        size: formatFileSize(file.size),
        dateAdded: new Date(),
      };

      // Update the form
      form.setValue("attachment", appFile);
    };

    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    form.setValue("attachment", undefined);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFileError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isSubmitting = status === "executing";

  return (
    <div>
      <h2 className='text-2xl font-semibold mb-4'>Send us a message</h2>

      {isSuccess ? (
        <motion.div
          className='flex flex-col items-center justify-center space-y-6 py-10'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <AnimatedCheckmark size={{ mobile: 120, desktop: 120 }} />
          <motion.h3
            className='text-xl font-medium text-center'
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Your message has been sent!
          </motion.h3>
          <motion.p
            className='text-center text-muted-foreground'
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            We&apos;ll get back to you as soon as possible.
          </motion.p>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Button onClick={handleNewMessage} className='mt-4'>
              Send Another Message
            </Button>
          </motion.div>
        </motion.div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    {prefilled.name ? (
                      <p className='py-2 px-3 rounded-md border border-input bg-background text-muted-foreground'>
                        {field.value}
                      </p>
                    ) : (
                      <Input
                        placeholder='Your name'
                        maxLength={100}
                        {...field}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    {prefilled.email ? (
                      <p className='py-2 px-3 rounded-md border border-input bg-background text-muted-foreground'>
                        {field.value}
                      </p>
                    ) : (
                      <Input
                        type='email'
                        placeholder='Your email'
                        maxLength={255}
                        {...field}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    {prefilled.phone ? (
                      <p className='py-2 px-3 rounded-md border border-input bg-background text-muted-foreground'>
                        {field.value}
                      </p>
                    ) : (
                      <PhoneInput
                        placeholder='Your phone'
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        defaultCountry='AU'
                        maxLength={20}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='company'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    {prefilled.company ? (
                      <p className='py-2 px-3 rounded-md border border-input bg-background text-muted-foreground'>
                        {field.value}
                      </p>
                    ) : (
                      <Input
                        type='text'
                        placeholder='Your company'
                        maxLength={100}
                        {...field}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='message'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Your message'
                      maxLength={2000}
                      {...field}
                      className='h-64'
                    />
                  </FormControl>
                  <FormDescription>
                    Please provide as much detail as possible.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* File Attachment Field */}
            <FormField
              control={form.control}
              name='attachment'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Attachment (optional)</FormLabel>
                  <FormControl>
                    <div className='flex flex-col gap-2'>
                      {!field.value && (
                        <div className='flex items-center'>
                          <input
                            type='file'
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className='hidden'
                            accept='.pdf,image/*'
                          />
                          <Button
                            type='button'
                            variant='outline'
                            onClick={() => fileInputRef.current?.click()}
                            className='flex items-center gap-2'
                          >
                            <Paperclip className='h-4 w-4' />
                            Add file
                          </Button>
                        </div>
                      )}

                      {fileError && (
                        <p className='text-destructive text-sm mt-1'>
                          {fileError}
                        </p>
                      )}

                      {field.value && (
                        <div className='flex items-center gap-2 p-2 border rounded-md'>
                          {field.value.type.toLowerCase() === "pdf" ? (
                            <File className='h-8 w-8 text-blue-500' />
                          ) : (
                            <div className='h-10 w-10 relative border overflow-hidden rounded-sm'>
                              <Image
                                src={field.value.url}
                                alt={field.value.name}
                                fill
                                className='object-cover'
                              />
                            </div>
                          )}
                          <div className='flex-1 min-w-0'>
                            <p className='font-medium truncate'>
                              {field.value.name}
                            </p>
                            <p className='text-sm text-gray-500'>
                              {field.value.size}
                            </p>
                          </div>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={removeFile}
                            className='h-8 w-8 p-0'
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormDescription>
                    Maximum file size: 5MB. Supported formats: PDF, JPG, PNG,
                    GIF.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type='submit' className='w-full' disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
